// "Avisarme cuando llegue" modal for products tagged 'proximamente' (snippets/proximamente-modal.liquid).
// Posts Shopify's newsletter form with fetch so the confirmation shows up inside the modal.
if (!customElements.get('proximamente-modal')) {
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  // If the visitor closes the hCaptcha challenge, the captcha never calls back.
  const CAPTCHA_TIMEOUT = 20000;

  customElements.define(
    'proximamente-modal',
    class ProximamenteModal extends HTMLElement {
      constructor() {
        super();

        this.dialog = this.querySelector('[role="dialog"]');
        this.form = this.querySelector('form');
        this.input = this.form.querySelector('input[type="email"]');
        this.submitButton = this.form.querySelector('[type="submit"]');
        this.errorMessage = this.querySelector('.proximamente-modal__error');
        this.formView = this.querySelector('[data-proximamente-view="form"]');
        this.successView = this.querySelector('[data-proximamente-view="success"]');
        this.opener = null;

        // Shopify's hCaptcha takes over /contact forms on first focus: it cancels the submit, gets a token
        // and then calls form.submit(), which reloads the page. It keeps the form.submit it finds when it
        // binds, so defining ours first makes it hand the token to the fetch instead.
        this.form.submit = () => this.send();

        this.form.addEventListener('submit', this.onSubmit.bind(this));
        this.input.addEventListener('input', this.clearError.bind(this));
        this.addEventListener('pointerdown', (event) => (this.pointerDownOnOverlay = event.target === this));
        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('keydown', this.onKeyDown.bind(this));
        // Capture phase: product cards are wrapped in a link, the click must not reach it.
        document.addEventListener('click', this.onOpenerClick.bind(this), true);
      }

      connectedCallback() {
        this.restorePostedState();
      }

      onOpenerClick(event) {
        const opener = event.target.closest('[data-proximamente-open]');
        if (!opener) return;

        event.preventDefault();
        event.stopPropagation();
        this.open(opener);
      }

      open(opener) {
        this.opener = opener;
        // Cards also live inside the cart drawer, which already locks the scroll.
        this.scrollWasLocked = document.body.classList.contains('overflow-hidden');
        this.removeAttribute('hidden');
        document.body.classList.add('overflow-hidden');
        this.dialog.focus();
      }

      close() {
        this.setAttribute('hidden', '');
        if (!this.scrollWasLocked) document.body.classList.remove('overflow-hidden');
        if (this.opener) this.opener.focus({ preventScroll: true });
        this.opener = null;
      }

      onClick(event) {
        const isOverlay = event.target === this && this.pointerDownOnOverlay;
        if (isOverlay || event.target.closest('[data-proximamente-close]')) this.close();
      }

      onKeyDown(event) {
        if (event.key === 'Escape') return this.close();
        if (event.key !== 'Tab') return;

        const focusable = Array.from(this.dialog.querySelectorAll('button, [href], input:not([type="hidden"])')).filter(
          (element) => element.offsetParent !== null
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && (document.activeElement === first || document.activeElement === this.dialog)) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      onSubmit(event) {
        event.preventDefault();
        // Keeps Shopify's captcha listeners from submitting the form natively, see the constructor.
        event.stopImmediatePropagation();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.input.value = this.input.value.trim();
        if (!EMAIL_PATTERN.test(this.input.value)) return this.showError('email');

        this.setLoading(true);
        const captcha = window.Shopify && window.Shopify.captcha;
        if (!captcha) return this.send();

        this.captchaTimeout = setTimeout(() => this.setLoading(false), CAPTCHA_TIMEOUT);
        captcha.protect(this.form, () => this.form.submit());
      }

      async send() {
        clearTimeout(this.captchaTimeout);
        this.setLoading(true);

        try {
          const response = await fetch(this.form.action, {
            method: 'POST',
            body: new URLSearchParams(new FormData(this.form)),
          });

          // Suspicious visitor or too many attempts: Shopify asks to solve the captcha on its own page.
          if (response.url.includes('/challenge')) return HTMLFormElement.prototype.submit.call(this.form);
          if (!response.ok) throw new Error(`Customer form responded ${response.status}`);

          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const status = html.querySelector(`#${this.form.id} [data-proximamente-status]`);
          if (status && status.dataset.proximamenteStatus === 'error') {
            return this.showError(status.dataset.proximamenteField === 'email' ? 'email' : 'server');
          }

          // An email that is already subscribed comes back without success nor errors.
          this.showSuccess();
        } catch (error) {
          console.error(error);
          this.showError('server');
        } finally {
          this.setLoading(false);
        }
      }

      // After a native post (captcha challenge fallback) Shopify reloads the page with the form's hash.
      restorePostedState() {
        if (window.location.hash !== `#${this.form.id}`) return;

        const status = this.form.querySelector('[data-proximamente-status]');
        if (!status) return;

        history.replaceState(null, '', window.location.pathname + window.location.search);
        this.open(null);
        if (status.dataset.proximamenteStatus === 'success') return this.showSuccess();
        this.showError(status.dataset.proximamenteField === 'email' ? 'email' : 'server');
      }

      setLoading(loading) {
        this.submitButton.classList.toggle('loading', loading);
        this.submitButton.setAttribute('aria-disabled', loading);
        this.submitButton.querySelector('.loading__spinner').classList.toggle('hidden', !loading);
      }

      showError(type) {
        this.errorMessage.textContent = type === 'email' ? this.dataset.errorEmail : this.dataset.errorServer;
        if (type !== 'email') return;

        this.input.setAttribute('aria-invalid', true);
        this.input.focus();
      }

      clearError() {
        this.errorMessage.textContent = '';
        this.input.removeAttribute('aria-invalid');
      }

      showSuccess() {
        this.formView.hidden = true;
        this.successView.hidden = false;
        this.successView.focus();
      }
    }
  );
}
